import React from "react";
import clsx from "clsx";
import { globalStore } from "store/GlobalStore";

interface TableCellTooltipProps {
  content?: string;
  children: React.ReactNode;
  className?: string;
}

const TOOLTIP_DELAY_MS = 400;

export default function TableCellTooltip({
  content,
  children,
  className,
}: TableCellTooltipProps) {
  const textRef = React.useRef<HTMLDivElement>(null);
  const setterTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();
  const removerTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(() => {
    return () => {
      clearTimeout(setterTimeoutRef.current);
      clearTimeout(removerTimeoutRef.current);
      globalStore.setTootipWrapperHover(false);
      globalStore.setTooltipContent(undefined);
    };
  }, []);

  const getTooltipContent = (): string | undefined => {
    if (content != null && content !== "") return content;
    const text = textRef.current?.textContent?.trim();
    return text || undefined;
  };

  const clearTooltip = () => {
    globalStore.setTootipWrapperHover(false);
    clearTimeout(setterTimeoutRef.current);
    setterTimeoutRef.current = undefined;

    removerTimeoutRef.current = setTimeout(() => {
      if (
        !globalStore.tooltip?.hoverOnTooltip ||
        globalStore.tooltip?.disableStickOnHover
      ) {
        globalStore.setTooltipContent(undefined);
      }
    }, 100);
  };

  const setTooltip = () => {
    const el = textRef.current;
    if (!el) return;

    const isTruncated = el.scrollWidth - el.clientWidth > 1;
    if (!isTruncated) return;

    globalStore.setTootipWrapperHover(true);
    clearTimeout(removerTimeoutRef.current);

    setterTimeoutRef.current = setTimeout(() => {
      if (!textRef.current || !globalStore.tootipWrapperHover) return;

      const tooltipContent = getTooltipContent();
      if (!tooltipContent) return;

      const { top, left, width } = textRef.current.getBoundingClientRect();

      globalStore.setTooltipContent({
        content: tooltipContent,
        position: {
          left: left + width / 2,
          top,
        },
        className: "text-xs whitespace-nowrap max-w-fit px-2 py-1.5",
        whiteTheme: true,
        disableStickOnHover: true,
        active: true,
      });
    }, TOOLTIP_DELAY_MS);
  };

  return (
    <div
      ref={textRef}
      onMouseEnter={setTooltip}
      onMouseLeave={clearTooltip}
      className={clsx("truncate min-w-0 w-full max-w-full", className)}
    >
      {children}
    </div>
  );
}
